import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTimeLong } from '../helpers/formatDateUTC';
import {
  Trash2,
  Clock,
  FileCode2,
  ListTodo,
  Loader2,
  AlertTriangle,
  CircleDot,
  CheckCircle2,
  Code2,
  Pencil,
  Check,
} from 'lucide-react';
import { Button } from './Button';
import { Badge } from './Badge';
import { useDeleteSession, useUpdateSession } from '../helpers/useSessionsApi';
import { toast } from 'sonner';
import { CollaborationSessions } from '../helpers/schema';
import { Selectable } from 'kysely';
import { AvatarWithStatus } from './AvatarWithStatus';
import { getTemplateConfig } from '../helpers/templateConfig';
import styles from './SessionCard.module.css';

function getTitleFontSize(title: string) {
  const len = title.length;
  if (len <= 25) return '0.85rem';
  if (len <= 40) return '0.78rem';
  if (len <= 60) return '0.70rem';
  return '0.63rem';
}

interface SessionParticipant {
  id: number;
  displayName: string;
  avatarUrl: string | null;
  isOnline: boolean;
}

interface SessionProgressInfo {
  fileCount: number;
  todoTotal: number;
  todoCompleted: number;
  lastActivityAt: string | null;
  aiStatus: 'idle' | 'coding' | 'queued' | 'error';
  activeTasks: number;
  errorCount: number;
}

interface SessionWithDetails extends Selectable<CollaborationSessions> {
  ownerDisplayName: string | null;
  ownerAvatarUrl: string | null;
  ownerIsOnline?: boolean;
  participants?: SessionParticipant[];
  progress?: SessionProgressInfo;
}

interface SessionCardProps {
  session: SessionWithDetails;
}

export const SessionCard = ({ session }: SessionCardProps) => {
  const deleteSession = useDeleteSession();
  const updateSession = useUpdateSession();
  const [isRenaming, setIsRenaming] = useState(false);
  const [editName, setEditName] = useState(session.name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const handleRename = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditName(session.name);
    setIsRenaming(true);
  };

  const handleSaveName = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== session.name) {
      updateSession.mutate(
        { id: session.id, name: trimmed },
        {
          onSuccess: () => toast.success('Renamed'),
          onError: () => toast.error('Failed to rename'),
        },
      );
    }
    setIsRenaming(false);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (confirm('Are you sure you want to delete this session? This action cannot be undone.')) {
      deleteSession.mutate(
        { id: session.id },
        {
          onSuccess: () => toast.success('Session deleted'),
          onError: () => toast.error('Failed to delete session'),
        },
      );
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'archived':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const templateConfig = getTemplateConfig(session.templateId);
  const progress = session.progress;

  // Calculate overall progress percentage
  const todoPercent =
    progress && progress.todoTotal > 0
      ? Math.round((progress.todoCompleted / progress.todoTotal) * 100)
      : null;

  // Determine project phase label based on available data
  const getProjectPhase = () => {
    if (!progress)
      return { label: 'Not started', icon: <CircleDot size={10} />, className: styles.statusIdle };
    if (progress.aiStatus === 'coding')
      return {
        label: 'AI Coding...',
        icon: <Loader2 size={10} className={styles.spinIcon} />,
        className: styles.statusCoding,
      };
    if (progress.aiStatus === 'queued')
      return {
        label: 'Queued',
        icon: <Loader2 size={10} className={styles.spinIcon} />,
        className: styles.statusQueued,
      };
    if (progress.aiStatus === 'error')
      return {
        label: `${progress.errorCount} error${progress.errorCount !== 1 ? 's' : ''}`,
        icon: <AlertTriangle size={10} />,
        className: styles.statusError,
      };
    if (progress.todoTotal > 0 && progress.todoCompleted === progress.todoTotal)
      return {
        label: 'Completed',
        icon: <CheckCircle2 size={10} />,
        className: styles.statusCompleted,
      };
    if (progress.fileCount > 0)
      return {
        label: 'In progress',
        icon: <Code2 size={10} />,
        className: styles.statusInProgress,
      };
    return { label: 'Not started', icon: <CircleDot size={10} />, className: styles.statusIdle };
  };

  const phase = getProjectPhase();

  // Determine the most recent timestamp to display
  const displayTimestamp = progress?.lastActivityAt || session.updatedAt || session.createdAt;

  return (
    <Link to={`/session/${session.id}`} className={styles.card}>
      <div className={styles.header}>
        <div className={styles.topRow}>
          <div className={styles.titleRow}>
            <div className={styles.titleWithTag}>
              {templateConfig && (
                <span
                  className={styles.frameworkTag}
                  style={{
                    color: templateConfig.color,
                    backgroundColor: templateConfig.bgColor,
                    borderColor: templateConfig.borderColor,
                  }}
                >
                  <span className={styles.frameworkIcon}>{templateConfig.icon}</span>
                  {templateConfig.name}
                </span>
              )}
              {isRenaming ? (
                <input
                  ref={inputRef}
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onBlur={handleSaveName}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveName();
                    if (e.key === 'Escape') setIsRenaming(false);
                  }}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  className={styles.title}
                  style={{ fontSize: getTitleFontSize(editName), border: '1px solid var(--primary)', borderRadius: 4, padding: '1px 4px', outline: 'none', background: 'var(--background)', width: '100%' }}
                  maxLength={100}
                />
              ) : (
                <h3 className={styles.title} style={{ fontSize: getTitleFontSize(session.name), display: 'flex', alignItems: 'center', gap: 4 }}>
                  {session.name}
                  <button
                    onClick={handleRename}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, opacity: 0.4, color: 'inherit', display: 'inline-flex' }}
                    title="Rename"
                  >
                    <Pencil size={10} />
                  </button>
                </h3>
              )}
            </div>
            <Badge variant={getStatusVariant(session.status)} className={styles.statusChip}>
              {session.status}
            </Badge>
          </div>
          <div className={styles.participants}>
            <AvatarWithStatus
              displayName={session.ownerDisplayName || 'Unknown'}
              avatarUrl={session.ownerAvatarUrl}
              status={session.ownerIsOnline ? 'online' : 'offline'}
              size="sm"
              className={styles.ownerAvatar}
            />
            {session.participants && session.participants.length > 0 && (
              <>
                {session.participants.slice(0, 3).map((participant) => (
                  <AvatarWithStatus
                    key={participant.id}
                    displayName={participant.displayName}
                    avatarUrl={participant.avatarUrl}
                    status={participant.isOnline ? 'online' : 'offline'}
                    size="sm"
                    className={styles.stackedAvatar}
                  />
                ))}
                {session.participants.length > 3 && (
                  <div className={styles.moreParticipants}>+{session.participants.length - 3}</div>
                )}
              </>
            )}
          </div>
        </div>
        <p className={styles.description}>{session.description || 'No description provided.'}</p>
      </div>

      {/* Progress indicator - always visible */}
      <div className={styles.progressSection}>
        {/* Status row: AI status + stats */}
        <div className={styles.progressHeader}>
          <span className={`${styles.statusBadge} ${phase.className}`}>
            {phase.icon}
            {phase.label}
          </span>
          {todoPercent !== null && <span className={styles.progressPercent}>{todoPercent}%</span>}
        </div>

        {/* Progress bar */}
        <div className={styles.progressBarTrack}>
          <div
            className={`${styles.progressBarFill} ${
              progress?.aiStatus === 'coding'
                ? styles.progressBarAnimated
                : progress?.aiStatus === 'error'
                  ? styles.progressBarError
                  : todoPercent === 100
                    ? styles.progressBarComplete
                    : ''
            }`}
            style={{
              width: `${todoPercent ?? (progress?.fileCount ? Math.min(progress.fileCount * 10, 50) : 0)}%`,
            }}
          />
        </div>

        {/* Stats row: files + tasks */}
        <div className={styles.progressStats}>
          <span className={styles.progressStat}>
            <FileCode2 size={10} />
            {progress?.fileCount ?? 0} {(progress?.fileCount ?? 0) === 1 ? 'file' : 'files'}
          </span>
          {progress && progress.todoTotal > 0 && (
            <span className={styles.progressStat}>
              <ListTodo size={10} />
              {progress.todoCompleted}/{progress.todoTotal} tasks
            </span>
          )}
          {progress && progress.errorCount > 0 && (
            <span className={`${styles.progressStat} ${styles.errorStat}`}>
              <AlertTriangle size={10} />
              {progress.errorCount} {progress.errorCount === 1 ? 'error' : 'errors'}
            </span>
          )}
        </div>
      </div>

      <div className={styles.footer}>
        <div className={styles.meta}>
          <Clock size={11} />
          <span>{formatDateTimeLong(displayTimestamp)}</span>
        </div>

        <div className={styles.actions}>
          <Button
            variant="ghost"
            size="icon-sm"
            className={styles.deleteBtn}
            onClick={handleDelete}
            title="Delete Session"
          >
            <Trash2 size={13} />
          </Button>
        </div>
      </div>
    </Link>
  );
};
